import React, { useMemo, useState } from 'react';
import { ServerIcon } from '../utils/serverIcons';
import { getDayMetric, getMetricTotal } from '../utils/mrpStatisticsMetrics';

function parseServerParts(server) {
  const s = server || '';
  const i = s.indexOf('_');
  return {
    ru: i >= 0 ? s.slice(0, i) : s || '—',
    name: i >= 0 ? s.slice(i + 1) : '',
  };
}

const DAY_FULL = ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

function MetricCell({
  value,
  metric,
  canEdit,
  editing,
  editValue,
  savingMetric,
  moderator,
  dayStat,
  onStartEdit,
  onEditValueChange,
  onSaveEdit,
  onCancelEdit,
  formatDuration,
  isTotal,
  size = 'md',
}) {
  const isDuration = metric.format === 'duration';
  const display = isDuration
    ? formatDuration(value)
    : (value > 0 ? String(value) : '—');

  const sizeClass = size === 'sm' ? 'mrp-v--sm' : size === 'lg' ? 'mrp-v--lg' : '';

  if (editing && !isDuration && dayStat) {
    return (
      <input
        className={`mrp-v-input ${sizeClass}`}
        type="number"
        min="0"
        max="999"
        value={editValue}
        autoFocus
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || (parseInt(v, 10) >= 0 && parseInt(v, 10) <= 999 && v.length <= 3)) {
            onEditValueChange(v);
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!savingMetric && editing) {
              onSaveEdit(moderator.discord_id, dayStat.day, metric.key);
            }
          }, 150);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancelEdit(); }
        }}
      />
    );
  }

  const classes = [
    'mrp-v',
    sizeClass,
    value > 0 ? 'has-value' : '',
    isTotal ? 'is-total' : '',
    isDuration ? 'is-time' : '',
  ].filter(Boolean).join(' ');

  if (isTotal || isDuration || !canEdit || !dayStat) {
    return <span className={classes}>{display}</span>;
  }

  return (
    <button
      type="button"
      className={`${classes} is-editable`}
      title={metric.label}
      onClick={() => onStartEdit(moderator.discord_id, dayStat.day, metric.key, value)}
    >
      {display}
    </button>
  );
}

function ManagerIdentity({ mod, serverIconMap }) {
  const server = parseServerParts(mod.server);
  return (
    <>
      <td className="mrp-c mrp-c--server">
        <div className="mrp-server">
          <div className="mrp-server__top">
            <ServerIcon iconUrl={serverIconMap[mod.server]} size={18} className="mrp-server__icon" />
            <span className="mrp-server__ru">{server.ru}</span>
          </div>
          {server.name ? <span className="mrp-server__name">{server.name}</span> : null}
        </div>
      </td>
      <td className="mrp-c mrp-c--nick">
        <span className="mrp-nick">{mod.nickname || '—'}</span>
      </td>
    </>
  );
}

function ManagerIdentityHeaders() {
  return (
    <>
      <th className="mrp-h mrp-h--server">Сервер</th>
      <th className="mrp-h mrp-h--nick">Никнейм</th>
    </>
  );
}

function TotalsStack({ mod, metrics, formatDuration }) {
  return (
    <div className="mrp-totals">
      {metrics.map((m) => {
        const total = getMetricTotal(mod, m.key);
        const display = m.format === 'duration'
          ? formatDuration(total)
          : (total > 0 ? String(total) : '—');
        return (
          <div
            key={m.key}
            className={`mrp-totals__row mrp-totals__row--${m.group || 'online'} ${total > 0 ? 'has-value' : ''}`}
            title={m.label}
          >
            <span className="mrp-totals__label">{m.label}</span>
            <span className={`mrp-totals__val ${m.format === 'duration' ? 'is-time' : ''}`}>{display}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Компактный переключатель: Неделя + дни */
function DayPillStrip({ weekDayHeaders, activeDay, onSelect }) {
  return (
    <div className="mrp-view-pills" role="tablist" aria-label="Вид таблицы">
      <button
        type="button"
        role="tab"
        aria-selected={activeDay === 'week'}
        className={`mrp-view-pills__btn mrp-view-pills__btn--week ${activeDay === 'week' ? 'is-active' : ''}`}
        onClick={() => onSelect('week')}
      >
        Неделя
      </button>
      {weekDayHeaders.map((header, idx) => {
        const n = idx + 1;
        const [dow, date] = header.split(' - ');
        return (
          <button
            type="button"
            role="tab"
            key={header}
            aria-selected={activeDay === n}
            className={`mrp-view-pills__btn ${activeDay === n ? 'is-active' : ''}`}
            onClick={() => onSelect(n)}
            title={`${DAY_FULL[n]} · ${date}`}
          >
            <span className="mrp-view-pills__dow">{dow}</span>
            <span className="mrp-view-pills__date">{date}</span>
          </button>
        );
      })}
    </div>
  );
}

export function MetricLegend({ metrics }) {
  const seen = new Set();
  const groups = [];
  metrics.forEach((m) => {
    const g = m.group || 'online';
    if (seen.has(g)) return;
    seen.add(g);
    const labels = {
      online: 'Онлайн',
      pun: 'Наказания',
      resp: 'Ответы',
      mod: 'Комментарии',
      tickets: 'Тикеты',
    };
    groups.push({ key: g, label: labels[g] || g });
  });

  return (
    <div className="mrp-legend">
      {groups.map((g) => (
        <span key={g.key} className={`mrp-legend__item mrp-legend__item--${g.key}`}>
          {g.label}
        </span>
      ))}
    </div>
  );
}

const StatisticsManagersTable = ({
  title,
  moderators,
  weekDayHeaders,
  metrics,
  canEdit,
  editingCell,
  editValue,
  savingMetric,
  onStartEdit,
  onEditValueChange,
  onSaveEdit,
  onCancelEdit,
  isVacationDay,
  serverIconMap,
  formatDuration,
  viewDay: viewDayProp,
  onViewDayChange,
}) => {
  const [localDay, setLocalDay] = useState('week');
  const activeDay = viewDayProp !== undefined ? viewDayProp : localDay;
  const setActiveDay = onViewDayChange || setLocalDay;
  const showDayPicker = viewDayProp === undefined;

  const dayIndex = useMemo(() => {
    if (activeDay === 'week') return null;
    return Number(activeDay) - 1;
  }, [activeDay]);

  if (!moderators.length) return null;

  const isWeek = activeDay === 'week';

  return (
    <section className="mrp-board">
      <div className="mrp-board__bar">
        <div className="mrp-board__heading">
          <h3 className="mrp-board__title">{title}</h3>
          <span className="mrp-board__count">{moderators.length}</span>
        </div>
        {showDayPicker && (
          <DayPillStrip
            weekDayHeaders={weekDayHeaders}
            activeDay={activeDay}
            onSelect={setActiveDay}
          />
        )}
        <MetricLegend metrics={metrics} />
      </div>

      <div className={`mrp-board__frame ${isWeek ? 'is-week' : 'is-day'}`}>
        <div className="mrp-board__scroll">
          {isWeek ? (
            <table className="mrp-sheet mrp-sheet--week">
              <thead>
                <tr>
                  <ManagerIdentityHeaders />
                  <th className="mrp-h mrp-h--totals">Итог недели</th>
                  {weekDayHeaders.map((header, idx) => {
                    const [dow, date] = header.split(' - ');
                    return (
                      <th
                        key={header}
                        className="mrp-h mrp-h--day"
                      >
                        <button
                          type="button"
                          className="mrp-h__daybtn"
                          onClick={() => setActiveDay(idx + 1)}
                          title={`Открыть ${DAY_FULL[idx + 1]}`}
                        >
                          <span className="mrp-h__dow">{dow}</span>
                          <span className="mrp-h__date">{date}</span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {moderators.map((mod) => (
                  <tr key={mod.discord_id} className={mod.deleted_at ? 'is-deleted' : ''}>
                    <ManagerIdentity mod={mod} serverIconMap={serverIconMap} />
                    <td className="mrp-c mrp-c--totals">
                      <TotalsStack mod={mod} metrics={metrics} formatDuration={formatDuration} />
                    </td>
                    {mod.dailyStats.map((dayStat, dayIdx) => {
                      const vacation = isVacationDay(mod.discord_id, dayStat.date);
                      return (
                        <td
                          key={dayStat.day}
                          className={[
                            'mrp-c', 'mrp-c--day',
                            vacation ? 'is-vacation' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <button
                            type="button"
                            className="mrp-daycard"
                            onClick={() => setActiveDay(dayIdx + 1)}
                            title={`Открыть ${DAY_FULL[dayIdx + 1]}`}
                          >
                            {metrics.map((m) => {
                              const value = getDayMetric(dayStat, m.key);
                              const display = m.format === 'duration'
                                ? formatDuration(value)
                                : (value > 0 ? String(value) : '—');
                              return (
                                <span
                                  key={m.key}
                                  className={`mrp-daycard__item mrp-daycard__item--${m.group || 'online'} ${value > 0 ? 'has-value' : ''}`}
                                  title={m.label}
                                >
                                  <span className="mrp-daycard__k">{m.label}</span>
                                  <span className={`mrp-daycard__n ${m.format === 'duration' ? 'is-time' : ''}`}>{display}</span>
                                </span>
                              );
                            })}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="mrp-sheet mrp-sheet--day">
              <thead>
                <tr>
                  <ManagerIdentityHeaders />
                  <th className="mrp-h mrp-h--totals">Итог недели</th>
                  {metrics.map((m) => (
                    <th
                      key={m.key}
                      className={`mrp-h mrp-h--metric mrp-h--${m.group || 'online'}`}
                      title={m.label}
                    >
                      <span className="mrp-h__label">{m.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {moderators.map((mod) => {
                  const dayStat = mod.dailyStats[dayIndex];
                  if (!dayStat) return null;
                  const vacation = isVacationDay(mod.discord_id, dayStat.date);
                  return (
                    <tr
                      key={mod.discord_id}
                      className={[
                        mod.deleted_at ? 'is-deleted' : '',
                        vacation ? 'is-vacation-row' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <ManagerIdentity mod={mod} serverIconMap={serverIconMap} />
                      <td className="mrp-c mrp-c--totals">
                        <TotalsStack mod={mod} metrics={metrics} formatDuration={formatDuration} />
                      </td>
                      {metrics.map((m) => {
                        const value = getDayMetric(dayStat, m.key);
                        const editing = canEdit
                          && m.format !== 'duration'
                          && editingCell
                          && editingCell.moderatorId === mod.discord_id
                          && editingCell.day === dayStat.day
                          && editingCell.field === m.key;

                        return (
                          <td
                            key={m.key}
                            className={`mrp-c mrp-c--metric mrp-c--${m.group || 'online'}`}
                          >
                            <MetricCell
                              value={value}
                              metric={m}
                              canEdit={canEdit}
                              editing={editing}
                              editValue={editing ? editValue : ''}
                              savingMetric={savingMetric}
                              moderator={mod}
                              dayStat={dayStat}
                              onStartEdit={onStartEdit}
                              onEditValueChange={onEditValueChange}
                              onSaveEdit={onSaveEdit}
                              onCancelEdit={onCancelEdit}
                              formatDuration={formatDuration}
                              size="lg"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export { DayPillStrip };
export default StatisticsManagersTable;
